import React, { useRef, useState } from "react";
import Button from "../../Shared/UI/Button/components/Button";
import Tooltip from "../../Shared/UI/Tooltip/components/Tooltip";
import BoldIcon from '../../Shared/UI/BoldIcon/components/BoldIcon';
import CitationsSectionIcon from '../../Shared/UI/CitationsSectionIcon/components/CitationsSectionIcon';
import EmphasisIcon from '../../Shared/UI/EmphasisIcon/components/EmphasisIcon';
import HiddenFileInput from '../../Shared/UI/HiddenFileInput/components/HiddenFileInput';
import ImageIcon from '../../Shared/UI/ImageIcon/components/ImageIcon';
import ItalicIcon from '../../Shared/UI/ItalicIcon/components/ItalicIcon';
import LargeSectionTitleIcon from '../../Shared/UI/LargeSectionTitleIcon/components/LargeSectionTitleIcon';
import LinkIcon from '../../Shared/UI/LinkIcon/components/LinkIcon';
import ParagraphIcon from '../../Shared/UI/ParagraphIcon/components/ParagraphIcon';
import SmallSectionTitleIcon from '../../Shared/UI/SmallSectionTitleIcon/components/SmallSectionTitleIcon';
import SubtitleIcon from '../../Shared/UI/SubtitleIcon/components/SubtitleIcon';
import SuperscriptIcon from '../../Shared/UI/SuperscriptIcon/components/SuperscriptIcon';
import TitleizeIcon from '../../Shared/UI/TitleizeIcon/components/TitleizeIcon';

import { applyFormatToSelectedRange, applyStyleToSelectedRange, applyLinkToSelectedRange, removeLinkFromSelectedRange, insertImageAtCursor, handleImageSelection, LinkPopover } from "../../RichTextEditor";

import styles from './Discourse.module.scss';

const DiscourseFormattingControls = ({ contentEditable, setState, activeStyleTypes = [], activeFormat, activeHref, editableContentId }) => {
  const imageInputRef = useRef(null);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);

  const handleImageSelect = async (event) => {
    await handleImageSelection(
      event,
      setState,
      (file, dataUrl) => {
        insertImageAtCursor({
          pendingImageData: dataUrl,
          pendingImageFileName: file.name,
          setState,
        });
      }
    );
  };

  return (
    <>
      {contentEditable && (
        <div className={styles.stylingTool}>
          <HiddenFileInput
            ref={imageInputRef}
            accept="image/png,image/gif,image/jpeg,image/jpg,image/webp"
            onChange={handleImageSelect}
          />
          <Tooltip content="Bold" position="bottom">
            <Button
              variant="secondary"
              icon={<BoldIcon />}
              iconOnly
              aria-label="Bold"
              pressed={activeStyleTypes.includes('b')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyStyleToSelectedRange({ style: { type: 'b' }, setState })}
            />
          </Tooltip>
          <Tooltip content="Italic" position="bottom">
            <Button
              variant="secondary"
              icon={<ItalicIcon />}
              iconOnly
              aria-label="Italic"
              pressed={activeStyleTypes.includes('i')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyStyleToSelectedRange({ style: { type: 'i' }, setState })}
            />
          </Tooltip>
          <LinkPopover
            isOpen={linkPopoverOpen}
            onClose={() => setLinkPopoverOpen(false)}
            onApply={(href) => {
              setLinkPopoverOpen(false);
              applyLinkToSelectedRange({ href, setState, editableContentId });
            }}
            onRemove={() => {
              setLinkPopoverOpen(false);
              removeLinkFromSelectedRange({ setState, editableContentId });
            }}
            initialHref={activeHref}
          >
            <Tooltip content="Link" position="bottom" disabled={linkPopoverOpen}>
              <Button
                variant="secondary"
                icon={<LinkIcon />}
                iconOnly
                aria-label="Link"
                pressed={activeStyleTypes.includes('a')}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setLinkPopoverOpen(!linkPopoverOpen)}
              />
            </Tooltip>
          </LinkPopover>
          <Tooltip content="Title" position="bottom">
            <Button
              variant="secondary"
              icon={<TitleizeIcon />}
              iconOnly
              aria-label="Title"
              pressed={activeFormat === 'DocumentTitle'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormatToSelectedRange({ format: { type: 'DocumentTitle' }, setState })}
            />
          </Tooltip>
          <Tooltip content="Subtitle" position="bottom">
            <Button
              variant="secondary"
              icon={<SubtitleIcon />}
              iconOnly
              aria-label="Subtitle"
              pressed={activeFormat === 'DocumentSubtitle'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormatToSelectedRange({ format: { type: 'DocumentSubtitle' }, setState })}
            />
          </Tooltip>
          <Tooltip content="Large Section Title" position="bottom">
            <Button
              variant="secondary"
              icon={<LargeSectionTitleIcon />}
              iconOnly
              aria-label="Large Section Title"
              pressed={activeFormat === 'DocumentSectionTitle'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormatToSelectedRange({ format: { type: 'DocumentSectionTitle', size: 2 }, setState })}
            />
          </Tooltip>
          <Tooltip content="Small Section Title" position="bottom">
            <Button
              variant="secondary"
              icon={<SmallSectionTitleIcon />}
              iconOnly
              aria-label="Small Section Title"
              pressed={activeFormat === 'DocumentSectionTitle'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormatToSelectedRange({ format: { type: 'DocumentSectionTitle', size: 3 }, setState })}
            />
          </Tooltip>
          <Tooltip content="Emphasis" position="bottom">
            <Button
              variant="secondary"
              icon={<EmphasisIcon />}
              iconOnly
              aria-label="Emphasis"
              pressed={activeFormat === 'DocumentEmphasis'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormatToSelectedRange({ format: { type: 'DocumentEmphasis' }, setState })}
            />
          </Tooltip>
          <Tooltip content="Paragraph" position="bottom">
            <Button
              variant="secondary"
              icon={<ParagraphIcon />}
              iconOnly
              aria-label="Paragraph"
              pressed={activeFormat === 'DocumentParagraph'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormatToSelectedRange({ format: { type: 'DocumentParagraph' }, setState })}
            />
          </Tooltip>
          <Tooltip content="Superscript" position="bottom">
            <Button
              variant="secondary"
              icon={<SuperscriptIcon />}
              iconOnly
              aria-label="Superscript"
              pressed={activeStyleTypes.includes('sup')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyStyleToSelectedRange({ style: { type: 'sup' }, setState })}
            />
          </Tooltip>
          <Tooltip content="Insert Image" position="bottom">
            <Button
              variant="secondary"
              icon={<ImageIcon />}
              iconOnly
              aria-label="Insert Image"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => imageInputRef.current?.click()}
            />
          </Tooltip>
          <Tooltip content="Citations Section" position="bottom">
            <Button
              variant="secondary"
              icon={<CitationsSectionIcon />}
              iconOnly
              aria-label="Citations Section"
              pressed={activeFormat === 'DocumentCitationsSection' || activeFormat === 'DocumentCitation'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormatToSelectedRange({ format: { type: 'DocumentCitationsSection' }, setState })}
            />
          </Tooltip>
        </div>
      )}
    </>
  );
};

export default DiscourseFormattingControls;
